import {TemplateRef} from '@angular/core';

// Local types instead of importing `ADTSettings` from 'angular-datatables/src/models/settings' —
// that's a deep path into the package's own source tree, not a published subpath export.
export interface RanaDtColumn {
    title?: string;
    data?: string | null;
    orderable?: boolean;
    visible?: boolean;
    className?: string;
    render?: (data: any, type: string, row: any, meta: any) => string | number;
    defaultContent?: string;
    // Shape must match angular-datatables' own ADTTemplateRef/ADTTemplateRefContext
    // (captureEvents is required there) so RanaDtOptions stays structurally
    // assignable to the DataTableDirective's [dtOptions] input (ADTSettings).
    ngTemplateRef?: { ref: TemplateRef<any>; context?: { captureEvents: Function; userData?: any } };
}

export interface RanaDtOptions {
    data?: any[];
    columns: RanaDtColumn[];
    columnDefs?: any[];
    order?: any[];
    pageLength?: number;
    lengthMenu?: any[];
    autoWidth?: boolean;
    destroy?: boolean;
    rowCallback?: (row: Node, data: any, index: number) => any;
    language?: Record<string, any>;

    [key: string]: any;
}

const RANA_DT_LANGUAGE = {
    search: 'Search records',
    lengthMenu: 'Show _MENU_ entries',
    info: 'Showing _START_ to _END_ of _TOTAL_ entries',
    infoEmpty: 'No entries to show',
    infoFiltered: '(filtered from _MAX_ total entries)',
    emptyTable: 'No entries to show',
    zeroRecords: 'No matching records found',
    paginate: {first: 'First', previous: 'Previous', next: 'Next', last: 'Last'}
};

// DataTables assigns rendered cell content via raw `td.innerHTML = val` (confirmed in
// node_modules/datatables.net/js/dataTables.js) — a plain `{data: 'field'}` column with no
// `render` is NOT HTML-escaped by default. Any column showing free-text user-supplied data
// (names, titles, descriptions, etc.) must explicitly escape via this helper in its `render`,
// or a malicious value (e.g. a public registration form's name field) becomes stored XSS.
export function escapeHtml(value: any): string {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

declare const $: any;

// By default DataTables reports its warnings with a blocking alert() ("Cannot reinitialise DataTable ..."),
// which interrupts members for a problem they can do nothing about. Log them to the console instead.
export function quietDataTableAlerts(): void {
    if (typeof $ === 'undefined' || !$.fn?.dataTable?.ext) return;
    $.fn.dataTable.ext.errMode = 'none';
    $(document).on('error.dt', (_e: any, _settings: any, techNote: any, message: any) => {
        console.warn('DataTables:', techNote, message);
    });
}

export function ranaDtOptions(overrides: Partial<RanaDtOptions> & { columns: RanaDtColumn[] }): RanaDtOptions {
    return {
        pageLength: 10,
        lengthMenu: [[10, 25, 50, -1], [10, 25, 50, 'All']],
        autoWidth: false,
        language: RANA_DT_LANGUAGE,
        ...overrides
    };
}
